// The right-side inspector. Whenever a (primary × weekday) cell is active it
// shows the primary entity (doctor or tenant) and the slots that land on that
// weekday. `entityType` ("doctor" | "tenant") flips the header copy and the
// per-row tag (the OTHER dimension — tenant tag when grouped by doctor, doctor
// label when grouped by tenant).
//
// Edit / delete / "add slot" / bulk-color / bulk-edit-all are passed in only
// for the "definitions" view — in "available" (live availability) mode the
// callbacks are omitted, so the rail renders inspect-only.

import { useEffect, useState } from "react";
import { Check, Copy, Paintbrush2, Pencil, Plus, SlidersHorizontal, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { resolveTenantDisplayName } from "@/lib/tenant-display";
import { HueAvatar, Tag, WeekStrip } from "./heatmap-bits";
import {
  deriveSharedColor,
  formatDuration,
  formatRange,
  normalizeHex,
  slotSession,
  slotWeekdays,
  WEEKDAY_LONG,
} from "./heatmap-utils";

// Small chip for the slot's visit subtype (firstVisit / followUp). The
// follow-up chip renders as "Follow-up+" when metadata._moreTests is true
// (the slot needs additional tests). Returns null when the slot has no
// subtype set so legacy slots render unchanged.
function subTypeBadge(slot) {
  const subType = String(slot?.subType || "").trim();
  if (subType === "firstVisit") {
    return (
      <span
        className="rounded-sm bg-blue-500/15 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-blue-700 dark:text-blue-300"
        title="First-visit slot"
      >
        1st visit
      </span>
    );
  }
  if (subType === "followUp") {
    const moreTests = slot?.metadata?._moreTests === true;
    return (
      <span
        className="rounded-sm bg-violet-500/15 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-violet-700 dark:text-violet-300"
        title={moreTests ? "Follow-up slot — more tests needed" : "Follow-up slot"}
      >
        {moreTests ? "Follow-up+" : "Follow-up"}
      </span>
    );
  }
  return null;
}

// Short cadence label for the row strip. Recurring slots get one of:
//   "repeats weekly"                         — weekly cadence only
//   "repeats weekly (+N dates)"              — weekly cadence + extra RDATEs
//   "repeats monthly"                        — monthly cadence only
//   "repeats monthly (+N dates)"             — monthly cadence + extra RDATEs
//   "repeats on N date(s)"                   — explicit-dates frequency
// based on recurrence.frequency (empty/missing => weekly for back-compat).
// Static slots get "one-off".
function recurrenceLabel(slot) {
  if (slot?.slotType !== "recurring") return "one-off";
  const frequency = String(slot?.recurrence?.frequency || "weekly").toLowerCase();
  if (frequency === "dates") {
    const count = Array.isArray(slot?.recurrence?.dates) ? slot.recurrence.dates.length : 0;
    if (count === 1) return "repeats on 1 date";
    return `repeats on ${count} dates`;
  }
  const base = frequency === "monthly" ? "repeats monthly" : "repeats weekly";
  const extras = Array.isArray(slot?.recurrence?.dates) ? slot.recurrence.dates.length : 0;
  if (extras === 0) return base;
  return `${base} (+${extras} ${extras === 1 ? "date" : "dates"})`;
}

// Bulk-color control for the day's visible slots: pick a color to apply to
// every slot on the active day, or reset them to the location-hue default.
function ColorControl({ slots, busy, onApplyColor }) {
  const [open, setOpen] = useState(false);
  const sharedColor = deriveSharedColor(slots);
  const [draft, setDraft] = useState(sharedColor || "#0ea5e9");

  useEffect(() => {
    if (open) setDraft(sharedColor || "#0ea5e9");
  }, [open, sharedColor]);

  const count = slots.length;

  const apply = () => {
    const normalized = normalizeHex(draft);
    if (!normalized) return;
    onApplyColor(normalized);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="secondary"
          size="ssm"
          className="[&_svg]:size-3"
          disabled={busy || count === 0}
        >
          <Paintbrush2 />
          Color
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-60 space-y-2 p-3">
        <p className="text-xs font-medium text-foreground">
          Color {count} {count === 1 ? "slot" : "slots"} on this day
        </p>
        <div className="flex items-center gap-2">
          <input
            type="color"
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            className="h-9 w-12 cursor-pointer rounded-md border border-border bg-transparent p-1"
            aria-label="Pick color"
          />
          <Input
            value={draft}
            onChange={(event) => setDraft(event.target.value.trim())}
            className="h-9 font-mono uppercase"
            maxLength={7}
            placeholder="#rrggbb"
          />
        </div>
        <div className="flex items-center justify-end gap-2 pt-1">
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-8"
            disabled={busy}
            onClick={() => {
              onApplyColor(null);
              setOpen(false);
            }}
          >
            Reset
          </Button>
          <Button
            type="button"
            size="sm"
            className="h-8"
            disabled={busy}
            onClick={apply}
          >
            Apply
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

export function DetailRail({
  entity,
  entityType = "doctor",
  weekday,
  slots = [],
  // Selection plumbing — the page owns the Set so the bulk handlers can read
  // the same source of truth. When `onToggleSlotSelected` is missing, the
  // rail renders read-only (no checkboxes, no select-all chip).
  selectedSlotIds,
  onToggleSlotSelected,
  onSetAllSelected,
  onSelectSession,
  slotSelectionKey,
  onAddSlot,
  onEditSlot,
  onDuplicateSlot,
  onDeleteSlot,
  onApplyColor,
  colorBusy,
  onBulkEdit,
  bulkEditBusy,
  onBulkDuplicate,
  bulkDuplicateBusy,
  onBulkDelete,
  bulkDeleteBusy,
  recentlyEditedIds,
  recentlyEditedFields = [],
}) {
  if (!entity) return null;
  const weekdayName = WEEKDAY_LONG[weekday - 1] || "";
  const editable = Boolean(onEditSlot);
  const hasRowActions = Boolean(onEditSlot || onDuplicateSlot || onDeleteSlot);
  const isTenantGrouping = entityType === "tenant";
  const hasRecentEdits =
    recentlyEditedIds &&
    typeof recentlyEditedIds.size === "number" &&
    recentlyEditedIds.size > 0;

  // Selection summary derived from the page's Set so this stays the single
  // source of truth. The "selectable" flag also gates the per-row checkbox.
  const selectionEnabled = Boolean(onToggleSlotSelected && slotSelectionKey);
  const resolveSelectionKey = selectionEnabled ? slotSelectionKey : () => "";
  const isSlotSelected = (slot) =>
    selectionEnabled
      ? Boolean(selectedSlotIds?.has(resolveSelectionKey(slot)))
      : true;
  const selectedSlots = selectionEnabled
    ? slots.filter((slot) => selectedSlotIds?.has(resolveSelectionKey(slot)))
    : slots;
  const selectedCount = selectedSlots.length;
  const totalCount = slots.length;
  const allSelected = totalCount > 0 && selectedCount === totalCount;
  const someSelected = selectedCount > 0 && selectedCount < totalCount;

  // AM/PM quick-select. Slots partition into a morning and afternoon session by
  // start time (see slotSession). The control only appears when the cell has
  // BOTH halves — otherwise "AM"/"PM" would just duplicate select-all/none.
  const sessionSelectEnabled = selectionEnabled && Boolean(onSelectSession);
  const amCount = sessionSelectEnabled
    ? slots.filter((slot) => slotSession(slot) === "am").length
    : 0;
  const pmCount = sessionSelectEnabled
    ? slots.filter((slot) => slotSession(slot) === "pm").length
    : 0;
  const selectedAmCount = selectedSlots.filter(
    (slot) => slotSession(slot) === "am",
  ).length;
  const selectedPmCount = selectedSlots.filter(
    (slot) => slotSession(slot) === "pm",
  ).length;
  // A session button reads "active" when the selection is exactly that session
  // (all of it, none of the other) — the state right after clicking it.
  const amActive =
    amCount > 0 && selectedAmCount === amCount && selectedPmCount === 0;
  const pmActive =
    pmCount > 0 && selectedPmCount === pmCount && selectedAmCount === 0;
  const showSessionSelect = amCount > 0 && pmCount > 0;

  return (
    <aside className="flex w-[300px] shrink-0 flex-col border-l border-border bg-muted/30">
      <div className="border-b border-border p-4">
        <div className="text-[10px] font-bold uppercase tracking-[0.1em] text-muted-foreground/60">
          Inspecting
        </div>
        <div className="mt-1.5 flex items-center gap-2">
          <HueAvatar name={entity.name} id={entity.id} size={26} />
          <div className="min-w-0">
            <div className="truncate text-[13px] font-semibold text-foreground">{entity.name}</div>
            <div className="text-[11px] text-muted-foreground">
              {isTenantGrouping ? `${weekdayName} slots` : `on ${weekdayName}s`}
            </div>
          </div>
        </div>
        {selectionEnabled && totalCount > 0 ? (
          /* Select-all chip: tri-state (all/some/none) and the current count.
             Bulk actions below read the selection set so the user controls the
             scope of every group action from this one control. */
          <div className="mt-3 flex items-center gap-2 text-[11px] text-muted-foreground">
            <button
              type="button"
              onClick={() => onSetAllSelected?.(!allSelected)}
              aria-checked={allSelected ? "true" : someSelected ? "mixed" : "false"}
              role="checkbox"
              className={cn(
                "inline-flex size-4 items-center justify-center rounded-[3px] border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1",
                allSelected
                  ? "border-primary bg-primary text-primary-foreground"
                  : someSelected
                    ? "border-primary/70 bg-primary/20 text-primary"
                    : "border-input bg-background hover:border-foreground/40",
              )}
              title={allSelected ? "Deselect all" : "Select all"}
              aria-label={allSelected ? "Deselect all slots" : "Select all slots"}
            >
              {allSelected ? (
                <Check className="size-3" strokeWidth={3} />
              ) : someSelected ? (
                <span className="block h-[2px] w-2 rounded-full bg-current" />
              ) : null}
            </button>
            <span className="tabular-nums">
              <span className="font-semibold text-foreground">{selectedCount}</span>
              {" of "}
              {totalCount} selected
            </span>
          </div>
        ) : null}
        {showSessionSelect ? (
          /* Session quick-select — one click to target the whole morning or
             afternoon. Only shown when the cell actually has both halves so it
             never duplicates the select-all control above. */
          <div className="mt-2 flex items-center gap-2 text-[11px] text-muted-foreground">
            <span className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground/60">
              Session
            </span>
            <div className="inline-flex overflow-hidden rounded-md border border-input">
              <button
                type="button"
                onClick={() => onSelectSession?.("am")}
                aria-pressed={amActive}
                className={cn(
                  "px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring",
                  amActive
                    ? "bg-primary text-primary-foreground"
                    : "bg-background text-muted-foreground hover:bg-muted hover:text-foreground",
                )}
                title={`Select the ${amCount} morning ${amCount === 1 ? "slot" : "slots"} (before 13:00)`}
              >
                AM
                <span className="ml-1 tabular-nums opacity-70">{amCount}</span>
              </button>
              <button
                type="button"
                onClick={() => onSelectSession?.("pm")}
                aria-pressed={pmActive}
                className={cn(
                  "border-l border-input px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring",
                  pmActive
                    ? "bg-primary text-primary-foreground"
                    : "bg-background text-muted-foreground hover:bg-muted hover:text-foreground",
                )}
                title={`Select the ${pmCount} afternoon ${pmCount === 1 ? "slot" : "slots"} (13:00 and later)`}
              >
                PM
                <span className="ml-1 tabular-nums opacity-70">{pmCount}</span>
              </button>
            </div>
          </div>
        ) : null}
        {onApplyColor || onBulkEdit || onBulkDuplicate || onBulkDelete ? (
          <div className="mt-2 flex flex-wrap items-center gap-2">
            {onApplyColor ? (
              <ColorControl
                slots={selectedSlots}
                busy={colorBusy}
                onApplyColor={onApplyColor}
              />
            ) : null}
            {onBulkEdit ? (
              <Button
                type="button"
                variant="secondary"
                size="ssm"
                className="[&_svg]:size-3"
                disabled={bulkEditBusy || selectedCount === 0}
                onClick={onBulkEdit}
                title={
                  totalCount === 0
                    ? "Pick a cell with slots first"
                    : selectedCount === 0
                      ? "Select at least one slot to bulk-edit"
                      : `Edit type, frequency, times and more on the ${selectedCount} selected ${selectedCount === 1 ? "slot" : "slots"}`
                }
              >
                <SlidersHorizontal />
                Group edit
                {selectionEnabled && selectedCount < totalCount ? (
                  <span className="ml-1 tabular-nums opacity-70">
                    ({selectedCount})
                  </span>
                ) : null}
              </Button>
            ) : null}
            {onBulkDuplicate ? (
              <Button
                type="button"
                variant="secondary"
                size="ssm"
                className="[&_svg]:size-3"
                disabled={bulkDuplicateBusy || selectedCount === 0}
                onClick={onBulkDuplicate}
                title={
                  totalCount === 0
                    ? "Pick a cell with slots first"
                    : selectedCount === 0
                      ? "Select at least one slot to duplicate"
                      : `Clone the ${selectedCount} selected ${selectedCount === 1 ? "slot" : "slots"}; optionally override fields on the copies`
                }
              >
                <Copy />
                Group duplicate
                {selectionEnabled && selectedCount < totalCount ? (
                  <span className="ml-1 tabular-nums opacity-70">
                    ({selectedCount})
                  </span>
                ) : null}
              </Button>
            ) : null}
            {onBulkDelete ? (
              <Button
                type="button"
                variant="secondary"
                size="ssm"
                className="text-destructive hover:text-destructive [&_svg]:size-3"
                disabled={bulkDeleteBusy || selectedCount === 0}
                onClick={onBulkDelete}
                title={
                  totalCount === 0
                    ? "Pick a cell with slots first"
                    : selectedCount === 0
                      ? "Select at least one slot to delete"
                      : `Delete the ${selectedCount} selected ${selectedCount === 1 ? "slot" : "slots"}`
                }
              >
                <Trash2 />
                Group delete
                {selectionEnabled && selectedCount < totalCount ? (
                  <span className="ml-1 tabular-nums opacity-70">
                    ({selectedCount})
                  </span>
                ) : null}
              </Button>
            ) : null}
          </div>
        ) : null}
        {hasRecentEdits ? (
          <div
            className="mt-3 rounded-md border border-emerald-500/40 bg-emerald-500/10 px-2.5 py-1.5 text-[11px] text-emerald-700 dark:text-emerald-300"
            aria-live="polite"
          >
            <span className="font-semibold">Just updated</span>
            {recentlyEditedFields.length > 0 ? (
              <>
                {": "}
                <span>{recentlyEditedFields.join(", ")}</span>
              </>
            ) : null}
            <span className="text-emerald-700/70 dark:text-emerald-300/70">
              {" "}
              on {recentlyEditedIds.size}{" "}
              {recentlyEditedIds.size === 1 ? "slot" : "slots"}.
            </span>
          </div>
        ) : null}
      </div>

      <ScrollArea className="min-h-0 flex-1">
        <div className="flex flex-col gap-1.5 p-3">
          {slots.length === 0 ? (
            <div className="rounded-md border border-dashed border-border p-4 text-center text-[11px] text-muted-foreground/60">
              No slots
            </div>
          ) : null}
          {slots.map((slot) => {
            const slotKey = slot.definitionId ?? slot.id;
            // Match against every id-shaped field we set when capturing the
            // affected slots so a freshly-refreshed slot still highlights.
            const wasRecentlyEdited =
              recentlyEditedIds &&
              ((slot.definitionId && recentlyEditedIds.has(slot.definitionId)) ||
                (slot.mongoSlotId && recentlyEditedIds.has(slot.mongoSlotId)) ||
                (slot.id && recentlyEditedIds.has(slot.id)));
            const isSelected = isSlotSelected(slot);
            return (
              <article
                key={slotKey}
                onDoubleClick={editable ? () => onEditSlot(slot) : undefined}
                title={editable ? "Double-click to edit" : undefined}
                className={cn(
                  "group relative rounded-md border border-border bg-card px-2.5 py-2 transition-colors",
                  editable && "cursor-pointer hover:border-foreground/20",
                  // Subtle dim when the user opted this row out of the bulk
                  // scope — keeps the visual answer to "what gets acted on?"
                  // legible at a glance.
                  selectionEnabled && !isSelected && "opacity-60",
                  wasRecentlyEdited &&
                    "border-emerald-500/60 bg-emerald-500/10 ring-1 ring-emerald-500/40",
                )}
              >
              {/* Action overlay — pinned to the card's top-right so the icons
                  never compete with the row content (weekstrip, recurrence
                  label, subtype badge) for horizontal space. Revealed on
                  hover/focus only; the small backdrop keeps the icons legible
                  when they pass over the duration label underneath. */}
              {hasRowActions ? (
                <div className="pointer-events-none absolute right-1 top-1 z-10 flex items-center gap-0.5 rounded-md border border-border/60 bg-card/95 px-1 py-0.5 opacity-0 shadow-sm backdrop-blur-sm transition-opacity duration-100 group-hover:pointer-events-auto group-hover:opacity-100 group-focus-within:pointer-events-auto group-focus-within:opacity-100">
                  {onEditSlot ? (
                    <button
                      type="button"
                      onClick={(event) => {
                        event.stopPropagation();
                        onEditSlot(slot);
                      }}
                      onDoubleClick={(event) => event.stopPropagation()}
                      title="Edit slot"
                      aria-label="Edit slot"
                      className="inline-flex size-5 items-center justify-center rounded text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                    >
                      <Pencil className="size-3.5" />
                    </button>
                  ) : null}
                  {onDuplicateSlot ? (
                    <button
                      type="button"
                      onClick={(event) => {
                        event.stopPropagation();
                        onDuplicateSlot(slot);
                      }}
                      onDoubleClick={(event) => event.stopPropagation()}
                      title="Duplicate slot — opens the editor pre-filled with this slot's values, saves as a new definition"
                      aria-label="Duplicate slot"
                      className="inline-flex size-5 items-center justify-center rounded text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                    >
                      <Copy className="size-3.5" />
                    </button>
                  ) : null}
                  {onDeleteSlot ? (
                    <button
                      type="button"
                      onClick={(event) => {
                        event.stopPropagation();
                        onDeleteSlot(slot);
                      }}
                      onDoubleClick={(event) => event.stopPropagation()}
                      title="Delete slot"
                      aria-label="Delete slot"
                      className="inline-flex size-5 items-center justify-center rounded text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                    >
                      <Trash2 className="size-3.5" />
                    </button>
                  ) : null}
                </div>
              ) : null}
              <div className="flex items-center gap-2">
                {selectionEnabled ? (
                  /* Native checkbox keeps it simple and accessible. Clicks are
                     stop-propagated so they don't trigger the card's
                     double-click-to-edit handler or the (intentional) overlay
                     dismissal. */
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => onToggleSlotSelected?.(slot)}
                    onClick={(event) => event.stopPropagation()}
                    onDoubleClick={(event) => event.stopPropagation()}
                    className="size-3.5 cursor-pointer accent-primary"
                    aria-label={`${isSelected ? "Deselect" : "Select"} slot`}
                    title={
                      isSelected
                        ? "Exclude this slot from bulk actions"
                        : "Include this slot in bulk actions"
                    }
                  />
                ) : null}
                <span className="font-mono text-xs font-semibold tabular-nums text-foreground">
                  {formatRange(slot.startAt, slot.endAt)}
                </span>
                {/* The tag shows the OTHER dimension from the primary grouping
                    so the row still names both axes. */}
                {isTenantGrouping
                  ? slot.doctorId
                    ? <Tag id={slot.doctorId}>{slot.doctor || slot.doctorShort || "Doctor"}</Tag>
                    : null
                  : slot.tenantId
                    ? <Tag id={slot.tenantId}>{slot.tenantName || resolveTenantDisplayName(slot._tenant) || "Clinic"}</Tag>
                    : null}
                {wasRecentlyEdited ? (
                  <span className="rounded-sm bg-emerald-500/20 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-emerald-700 dark:text-emerald-300">
                    Updated
                  </span>
                ) : null}
                {/* Duration sits flush-right; on hover the action overlay sits
                    on top of it with a backdrop, intentionally hiding it
                    while actions are reachable. */}
                <span className="ml-auto text-[10px] tabular-nums text-muted-foreground/60">
                  {formatDuration(slot.startAt, slot.endAt)}
                </span>
              </div>
              <div className="mt-1.5 flex items-center gap-1.5">
                <WeekStrip active={slotWeekdays(slot)} id={entity.id} size="md" />
                {/* min-w-0 lets the long recurrence string truncate inside the
                    flex row instead of pushing the subtype badge off the
                    card. */}
                <span className="min-w-0 flex-1 truncate text-[10px] text-muted-foreground/60">
                  {recurrenceLabel(slot)}
                </span>
                {subTypeBadge(slot)}
              </div>
            </article>
            );
          })}
          {onAddSlot ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="mt-1 w-full justify-center [&_svg]:size-3.5"
              onClick={onAddSlot}
            >
              <Plus />
              Add slot to this day
            </Button>
          ) : null}
        </div>
      </ScrollArea>
    </aside>
  );
}
